import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
    ResponsiveContainer,
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    Tooltip as ReTooltip,
    CartesianGrid,
    ReferenceArea,
} from "recharts";

import { withTrailingMean } from "../common/Sparkline";

const CHART_HEIGHT = 200;

const day = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const month = new Intl.DateTimeFormat("en-GB", { month: "short", year: "2-digit" });
const formatDay = (iso) => day.format(new Date(iso));
const formatMonth = (iso) => month.format(new Date(iso));

/**
 * The workspace's day on each of `metrics` (`{ key, label, color, daily(day), format(value), exact(value), lowerIsBetter }`)
 * over the days any of them has. One is drawn with its axis and the 7-day mean dashed over it; several
 * are drawn as lines, each on its own scale, a lower-is-better one upside down. Dragging the mouse across
 * two days or more hands `onRange` the first and last day dragged over; a touch does not drag.
 */
export default function TrafficOverview({ traffic, metrics, onRange }) {
    const { days, data } = useMemo(() => {
        const charted = traffic.filter((point) => metrics.some((metric) => metric.daily(point) != null));
        const days = charted.map((point) => point.date);
        const data = metrics.length === 1
            ? withTrailingMean(charted.map((point) => metrics[0].daily(point)), days)
            : charted.map((point) => ({ day: point.date, ...Object.fromEntries(metrics.map((metric) => [metric.key, metric.daily(point)])) }));
        return { days, data };
    }, [traffic, metrics]);
    const [single] = metrics.length === 1 ? metrics : [];
    const exactOf = Object.fromEntries(metrics.map((metric) => [metric.label, metric.exact]));

    const [dragFrom, setDragFrom] = useState(null);
    const [dragTo, setDragTo] = useState(null);
    const dragRef = useRef(null);
    // Recharts hands a touch to these handlers too, as the touch point rather than a mouse event.
    const onMouseDown = (state, event) => {
        if (!onRange || event?.type !== "mousedown" || !state?.activeLabel) return;
        // Otherwise the drag selects the page's text, and the next drag picks that selection up instead.
        event.preventDefault();
        dragRef.current = state.activeLabel;
        setDragFrom(state.activeLabel);
        setDragTo(null);
    };
    const onMouseMove = (state) => {
        if (!dragRef.current || !state?.activeLabel) return;
        setDragTo(state.activeLabel);
    };
    const endDrag = useCallback(() => {
        dragRef.current = null;
        setDragFrom(null);
        setDragTo(null);
    }, []);
    const onMouseUp = () => {
        const from = dragRef.current;
        if (from != null && dragTo != null && dragTo !== from) {
            const [first, last] = from < dragTo ? [from, dragTo] : [dragTo, from];
            onRange(first, last);
        }
        endDrag();
    };
    // A release outside the chart never reaches `onMouseUp`; the window still hears it, and ends the drag without a range.
    useEffect(() => {
        if (dragFrom === null) return undefined;
        window.addEventListener("mouseup", endDrag);
        return () => window.removeEventListener("mouseup", endDrag);
    }, [dragFrom, endDrag]);
    const isDragging = dragFrom !== null && dragTo !== null;

    return (
        <Card>
            <Head>
                <Label>{metrics.map((metric) => metric.label).join(" · ")}, {formatDay(days[0])} – {formatDay(days[days.length - 1])}</Label>
            </Head>

            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
                               onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
                               style={onRange && { cursor: isDragging ? "col-resize" : "crosshair" }}>
                    {single && (
                        <defs>
                            <linearGradient id="traffic-fill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={single.color} stopOpacity={0.18} />
                                <stop offset="100%" stopColor={single.color} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                    )}
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={formatMonth} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false}
                           axisLine={{ stroke: "#e2e8f0" }} minTickGap={40} />
                    <ReTooltip
                        content={isDragging ? () => null : undefined}
                        contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: "0.78rem" }}
                        labelStyle={{ color: "#64748b" }}
                        labelFormatter={formatDay}
                        formatter={(value, name) => [value == null ? "–" : (exactOf[name] ?? exactOf[single?.label])(value), name]}
                    />
                    {single && (
                        <YAxis reversed={single.lowerIsBetter} domain={single.lowerIsBetter ? ["dataMin", "dataMax"] : [0, "auto"]}
                               tickFormatter={single.format}
                               tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={44} />
                    )}
                    {isDragging && (
                        <ReferenceArea x1={dragFrom < dragTo ? dragFrom : dragTo} x2={dragFrom < dragTo ? dragTo : dragFrom}
                                       yAxisId={single ? 0 : metrics[0].key}
                                       fill="#6366f1" fillOpacity={0.15} stroke="#6366f1" strokeOpacity={0.6} strokeWidth={1} />
                    )}
                    {single && (
                        <Area type="monotone" dataKey="v" name={single.label} stroke={single.color} strokeOpacity={0.45} strokeWidth={1}
                              fill="url(#traffic-fill)" dot={false} isAnimationActive={false} />
                    )}
                    {single && (
                        <Line type="monotone" dataKey="avg" name="7-day mean" stroke={single.color} strokeWidth={1.75}
                              strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                    )}
                    {!single && metrics.map((metric) => (
                        <YAxis key={metric.key} yAxisId={metric.key} hide reversed={metric.lowerIsBetter}
                               domain={metric.lowerIsBetter ? ["dataMin", "dataMax"] : [0, "auto"]} />
                    ))}
                    {!single && metrics.map((metric) => (
                        <Line key={metric.key} yAxisId={metric.key} type="monotone" dataKey={metric.key} name={metric.label} stroke={metric.color}
                              strokeWidth={1.5} dot={false} isAnimationActive={false} />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>

            <Legend>
                {single
                    ? <>
                        <Key><Swatch $color={single.color} $faint />{single.label} a day</Key>
                        <Key><Swatch $color={single.color} $dashed />7-day mean</Key>
                    </>
                    : metrics.map((metric) => <Key key={metric.key}><Swatch $color={metric.color} />{metric.label}{metric.lowerIsBetter && " (inverted)"}</Key>)}
                {!single && <Note>each line on its own scale</Note>}
            </Legend>
        </Card>
    );
}

const Card = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    padding: 18px 22px 14px;
    margin-bottom: 18px;
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
        padding: 16px 14px 12px;
    }
`;

const Head = styled.div`
    margin-bottom: 10px;
`;

const Label = styled.div`
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #64748b;
`;

/** One entry per line drawn, under the chart. */
const Legend = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 16px;
    margin-top: 8px;
    font-size: 0.72rem;
    color: #475569;
`;

const Key = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

/** The line as drawn: solid, faint for the daily area under a mean, or dashed for the mean. */
const Swatch = styled.span`
    width: 16px;
    border-top: 2px ${({ $dashed }) => ($dashed ? "dashed" : "solid")} ${({ $color }) => $color};
    opacity: ${({ $faint }) => ($faint ? 0.45 : 1)};
`;

const Note = styled.span`
    color: #94a3b8;
`;
